/**
 * PlaybackEngine component (Task 7.3, updated Task 1.4)
 *
 * Manages playback animation loop using requestAnimationFrame.
 *
 * Features:
 * - Subscribes to timelineStore isPlaying state
 * - Uses requestAnimationFrame for smooth 60fps updates
 * - Increments playheadPosition based on playbackSpeed
 * - Stops at end of last tool (not timeline duration) or loops if enabled
 * - Triggers 3D animation updates via playhead changes
 */

import { useEffect, useRef } from 'react';
import { useTimelineStore, getGroupDuration } from '../../stores/timelineStore';

export interface PlaybackEngineProps {
  /**
   * Whether to enable loop playback
   * When enabled, playback restarts from beginning when timeline ends
   * @default false
   */
  loopEnabled?: boolean;
}

/**
 * Calculate the end time for playback based on tools and placed groups on the timeline.
 * Returns the end time of the last tool or placed group, or 0 if empty.
 *
 * For placed groups, end time = startTime + getGroupDuration(group)
 *
 * @returns The maximum end time across all tools and placed groups, or 0 if empty
 */
function calculateToolsEndTime(): number {
  const { tools, placedGroups, groups } = useTimelineStore.getState();

  // Calculate max end time for direct tools
  const toolsEndTime =
    tools.length > 0
      ? Math.max(0, ...tools.map((t) => t.startTime + t.duration))
      : 0;

  // Calculate max end time for placed groups
  let groupsEndTime = 0;
  for (const placedGroup of placedGroups) {
    const group = groups.find((g) => g.id === placedGroup.groupId);
    if (group) {
      const groupEndTime = placedGroup.startTime + getGroupDuration(group);
      groupsEndTime = Math.max(groupsEndTime, groupEndTime);
    }
  }

  // Return the maximum of both
  return Math.max(toolsEndTime, groupsEndTime);
}

/**
 * PlaybackEngine component
 *
 * This component doesn't render anything visible - it's purely for managing
 * the playback animation loop. It must be mounted in the app to enable playback.
 */
export function PlaybackEngine({ loopEnabled = false }: PlaybackEngineProps) {
  const isPlaying = useTimelineStore((state) => state.isPlaying);
  const playheadPosition = useTimelineStore((state) => state.playheadPosition);
  const setPlayheadPosition = useTimelineStore(
    (state) => state.setPlayheadPosition
  );
  const stopPlayback = useTimelineStore((state) => state.stopPlayback);

  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);

  useEffect(() => {
    // If not playing, clear any active animation frame and reset lastTime
    if (!isPlaying) {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      lastTimeRef.current = null;
      return;
    }

    // Start animation loop
    const animate = (currentTime: number) => {
      // Initialize lastTime on first frame
      if (lastTimeRef.current === null) {
        lastTimeRef.current = currentTime;
      }

      // Calculate delta time in seconds
      const deltaTime = (currentTime - lastTimeRef.current) / 1000;
      lastTimeRef.current = currentTime;

      // Get current state
      const currentPosition = useTimelineStore.getState().playheadPosition;
      const currentSpeed = useTimelineStore.getState().playbackSpeed;

      // Calculate end time based on last tool (not timeline duration)
      const endTime = calculateToolsEndTime();

      // Calculate new position
      const increment = deltaTime * currentSpeed;
      let newPosition = currentPosition + increment;

      // Handle end of playback (at last tool's end time)
      if (newPosition >= endTime) {
        if (loopEnabled) {
          // Loop back to start
          newPosition = 0;
        } else {
          // Stop at end of last tool
          newPosition = endTime;
          useTimelineStore.getState().stopPlayback();
        }
      }

      // Update playhead position
      setPlayheadPosition(newPosition);

      // Continue animation loop if still playing
      if (useTimelineStore.getState().isPlaying) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    // Start the animation loop
    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup on unmount or when isPlaying changes
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      lastTimeRef.current = null;
    };
  }, [isPlaying, loopEnabled, setPlayheadPosition, stopPlayback]);

  // Also stop playback when playhead reaches the end manually (e.g., via scrubbing)
  useEffect(() => {
    const endTime = calculateToolsEndTime();
    if (
      playheadPosition >= endTime &&
      isPlaying &&
      !loopEnabled &&
      endTime > 0
    ) {
      stopPlayback();
    }
  }, [playheadPosition, isPlaying, loopEnabled, stopPlayback]);

  // This component doesn't render anything
  return null;
}
